// Ticket local del POS: datos planos (se guardan inmutables en la cola) y su versión ESC/POS en base64
// para localhost:5000/imprimir-base64 (el mismo camino que el ticket del servidor). Sin logo.
const ANCHO = 48;

// Solo ASCII imprimible: la impresora queda con su code page por defecto (sin tildes ni ñ)
const ascii = (t) => String(t === null || t === undefined ? '' : t).normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^\x20-\x7E]/g, ' ');
const izqDer = (izq, der) => { const i = ascii(izq); const d = ascii(der); return i.slice(0, ANCHO - d.length - 1).padEnd(ANCHO - d.length) + d; };
const dinero = (n) => (Number(n) || 0).toFixed(2);

// Ítem 20 (parte A): igual que wordwrap($texto, $ancho, salto, true) del backend (ImpresionService): corta en espacios y
// parte las palabras más largas que el ancho. Devuelve las líneas.
export function envolverTexto(texto, ancho) {
  const palabras = String(texto === null || texto === undefined ? '' : texto).split(' ');
  const lineas = [];
  let actual = '';
  palabras.forEach((p) => {
    let palabra = p;
    while (palabra.length > ancho) {
      if (actual) { lineas.push(actual); actual = ''; }
      lineas.push(palabra.slice(0, ancho));
      palabra = palabra.slice(ancho);
    }
    if (!actual) actual = palabra;
    else if ((actual + ' ' + palabra).length <= ancho) actual += ' ' + palabra;
    else { lineas.push(actual); actual = palabra; }
  });
  lineas.push(actual);
  return lineas;
}

// Ítem 20: `glosa` y `encabezado` (bytes ESC/POS del encabezado en base64, del catálogo) son opcionales: las ventas guardadas
// antes del cambio no los tienen y se imprimen como antes.
// Antes:
// export function armarTicket({ items, subtotal, descuento, total, totalEnLetras, cliente, nit, canal, metodoPago, caja, sucursal, cajero, fecha }) {
//   return { items, subtotal, descuento, total, totalEnLetras, cliente, nit, canal, metodoPago, caja, sucursal, cajero, fecha };
// }
export function armarTicket({ items, subtotal, descuento, total, totalEnLetras, cliente, nit, canal, metodoPago, caja, sucursal, cajero, fecha, glosa, encabezado }) {
  return { items, subtotal, descuento, total, totalEnLetras, cliente, nit, canal, metodoPago, caja, sucursal, cajero, fecha, glosa, encabezado };
}

// «EN FECHA» del ticket del backend: d/m/Y H:i:s (hora de impresión)
const dosDig = (n) => String(n).padStart(2, '0');
const fechaImpresion = (d = new Date()) => `${dosDig(d.getDate())}/${dosDig(d.getMonth() + 1)}/${d.getFullYear()} ${dosDig(d.getHours())}:${dosDig(d.getMinutes())}:${dosDig(d.getSeconds())}`;

// Bytes del encabezado del backend (base64 → cadena binaria) sin el salto de línea final; null si no hay o no se puede leer
function encabezadoBinario(base64) {
  if (!base64) return null;
  try { return atob(base64).replace(/\n$/, ''); } catch (e) { return null; }
}

// estadoAlImprimir: 'pendiente' | 'sincronizada' (solo cambia el pie)
export function ticketAEscPosBase64(ticket, estadoAlImprimir = 'pendiente') {
  const ESC = '\x1b'; const GS = '\x1d';
  const lineas = [];
  const centro = ESC + 'a\x01'; const izquierda = ESC + 'a\x00';
  const sep = '-'.repeat(ANCHO);
  // Ítem 20: con el encabezado guardado del catálogo (logo + NOTA DE VENTA + SIN DERECHO A CREDITO FISCAL + sucursal + dirección +
  // ciudad + guiones) se antepone TAL CUAL al cuerpo; sin él (catálogo sin encabezado), se imprime como antes y nunca se bloquea.
  // Antes: lineas.push(ESC + '@', centro, ESC + 'E\x01', 'NOTA DE VENTA - TICKET LOCAL', ESC + 'E\x00', ascii(ticket.sucursal), sep, izquierda);
  const encabezado = encabezadoBinario(ticket.encabezado);
  if (encabezado) lineas.push(encabezado, izquierda);
  else lineas.push(ESC + '@', centro, ESC + 'E\x01', 'NOTA DE VENTA - TICKET LOCAL', ESC + 'E\x00', '(SIN DERECHO A CREDITO FISCAL)', ascii(ticket.sucursal), sep, izquierda);
  lineas.push(izqDer(`TICKET LOCAL N. ${String(ticket.correlativo_local).padStart(4, '0')}`, `Caja ${ticket.caja}`));
  lineas.push(izqDer(`Ref.: ${ticket.referencia}`, ticket.fecha));
  lineas.push(ascii(`SENOR(ES): ${ticket.cliente}`).slice(0, ANCHO), ascii(`NIT/CI: ${ticket.nit}`), sep);
  lineas.push('CANT DETALLE                    P.UNIT    TOTAL');
  ticket.items.forEach((it) => {
    // Ítem 20 (parte A): la línea única de un 2x1 («envolver») se parte en varias líneas como en el ticket del backend
    // (primera línea con los precios, el resto del nombre debajo). Antes, para todas las líneas, solo lo siguiente:
    // const cant = String(it.cantidad).padEnd(4); const det = ascii(it.nombre).slice(0, 24).padEnd(24);
    // lineas.push(`${cant} ${det} ${dinero(it.precio).padStart(8)} ${dinero(it.total).padStart(8)}`.slice(0, ANCHO));
    const partes = it.envolver ? envolverTexto(ascii(it.nombre), 24) : [ascii(it.nombre).slice(0, 24)];
    const cant = String(it.cantidad).padEnd(4); const det = partes[0].padEnd(24);
    lineas.push(`${cant} ${det} ${dinero(it.precio).padStart(8)} ${dinero(it.total).padStart(8)}`.slice(0, ANCHO));
    partes.slice(1).forEach((resto) => lineas.push(('     ' + resto).slice(0, ANCHO)));
    (it.detalle || []).forEach((d) => lineas.push(('     ' + ascii(d)).slice(0, ANCHO)));
  });
  lineas.push(sep, izqDer('SUBTOTAL:', dinero(ticket.subtotal)));
  if (Number(ticket.descuento) > 0) lineas.push(izqDer('DESC.:', dinero(ticket.descuento)));
  lineas.push(ESC + 'E\x01' + izqDer('TOTAL Bs.:', dinero(ticket.total)) + ESC + 'E\x00');
  lineas.push(ascii(`SON: ${ticket.totalEnLetras}`).slice(0, ANCHO), sep);
  // Ítem 20: pie como el del backend (IMPRESO POR, EN FECHA, GLOSA, canal, método de pago). Antes:
  // lineas.push(ascii(`CANAL DE VENTA: ${ticket.canal}`), ascii(`METODO PAGO: ${ticket.metodoPago}`), ascii(`CAJERO: ${ticket.cajero}`), sep, centro);
  lineas.push(ascii(`IMPRESO POR: ${ticket.cajero}`).slice(0, ANCHO), `EN FECHA: ${fechaImpresion()}`);
  if (ticket.glosa) lineas.push(ascii(`GLOSA: ${ticket.glosa}`).slice(0, ANCHO));
  lineas.push(ascii(`CANAL DE VENTA: ${ticket.canal}`), ascii(`METODO PAGO: ${ticket.metodoPago}`), sep, centro);
  lineas.push(estadoAlImprimir === 'sincronizada' ? 'VENTA REGISTRADA' : 'PENDIENTE DE SINCRONIZAR');
  lineas.push('!GRACIAS POR SU COMPRA!', '\n\n\n', GS + 'V\x00');
  return btoa(lineas.join('\n'));
}
